/**
 * teamShareProgressStore — the web's view of one in-flight review-link share (Create Review Link /
 * Update), fed by Unity's `teamUploadProgress` verb.
 *
 * WHY A LEAF STORE. The share's lifecycle already lives in bridge/features/share.ts as a parked
 * promise (`pendingTeamShare`): resolved by `teamAccessState`, rejected by `teamShareError`, or
 * dropped by its own 5-minute timeout. Nothing rendered could see that promise, which is why the
 * only feedback a sharing user ever had was ShareDialog's button label flipping to "Creating...".
 * On the board route Unity keeps the WebView visible for the whole share (it used to hide it, which
 * is where the black screen and the exposed "Please wait" blocker came from), so the web now owns
 * the wait state: TeamShareOverlay reads this store and paints the scrim.
 *
 * OWNERSHIP OF `active`. Only the promise's park/settle sites in share.ts flip it — `begin()` when
 * the request is parked, `end()` on resolve, reject and timeout. `report()` NEVER raises it: a
 * progress message with no parked promise (an older/newer Unity emitting for a share the web did
 * not start) would otherwise paint a scrim that nothing is on the hook to take down.
 *
 * Stages are Unity's strings, passed through untranslated and never rendered: TeamAccessUploader's
 * "requesting-token" … "finalizing" plus the two share-preparation stages emitted before upload
 * ("preparing-thumbnails", "preparing"). The overlay only counts — `current`/`total` are 0/0 when
 * the stage has nothing to count — so a stage this file has never heard of degrades to the bare
 * "Sharing project…" line, never to a blank one.
 */
import { create } from 'zustand';

export interface TeamUploadProgressPayload {
  stage: string;
  current: number;
  total: number;
  message: string;
}

export interface TeamShareProgressState {
  /** A share request is parked and unanswered. Sole writers: begin()/end(). */
  active: boolean;
  stage: string | null;
  current: number;
  total: number;
  message: string;
  /** Called when share.ts parks the promise. Resets the counters so a previous share's last
   *  "n of m" cannot flash before the new share's first progress message lands. */
  begin: () => void;
  /** The `teamUploadProgress` verb. Counters only — see OWNERSHIP OF `active`. */
  report: (p: Partial<TeamUploadProgressPayload> | null | undefined) => void;
  /** Called when the promise settles (resolve, reject, timeout). */
  end: () => void;
}

const IDLE = { active: false, stage: null, current: 0, total: 0, message: '' } as const;

const asCount = (v: unknown): number =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0;

export const useTeamShareProgressStore = create<TeamShareProgressState>((set) => ({
  ...IDLE,
  begin: () => set({ active: true, stage: null, current: 0, total: 0, message: '' }),
  report: (p) => {
    // Hand-built JSON on the Unity side, so every field is defensively read: a malformed
    // message must not throw inside the dispatch table or poison the counters with NaN.
    if (!p || typeof p !== 'object') return;
    set({
      stage: typeof p.stage === 'string' ? p.stage : null,
      current: asCount(p.current),
      total: asCount(p.total),
      message: typeof p.message === 'string' ? p.message : '',
    });
  },
  end: () => set({ ...IDLE }),
}));
